const getDaysTillGoalDate = (values, isMetric) => {
  const { weightKg, weightLbs, targetWeightKg, targetWeightLbs, goalPaceKg, goalPaceLbs } = values

  // Goal data calculation provided by the product team:
  // ((weightKg - targetWeightKg - 5kg or 1/3 of current weight for the 1st week (whichever is smaller)) / xkg/week  + 1) * 7 = days to reach goal
  // Example:
  // ((90 - 78 - 5 [because 90/3 > 5kg]) / 2 + 1) * 7 = 35 days

  let daysTillGoalDate

  if (isMetric) {
    const whenWeightGainKg = goalPaceKg || 1
    const weightDiff = Math.abs(weightKg - targetWeightKg)
    const initialLoss = Math.min(5, weightDiff / 3)
    const remainingWeight = weightDiff - initialLoss
    daysTillGoalDate = ((remainingWeight / whenWeightGainKg) + 1) * 7
  } else {
    const whenWeightGainLbs = goalPaceLbs || 1
    const weightDiff = Math.abs(weightLbs - targetWeightLbs)
    const initialLoss = Math.min(11, weightDiff / 3)
    const remainingWeight = weightDiff - initialLoss
    daysTillGoalDate = ((remainingWeight / whenWeightGainLbs) + 1) * 7
  }

  return Math.abs(daysTillGoalDate)
}

const getDaysTillDate = (eventDate) => {
  if (!eventDate) return 0

  const today = new Date()
  const date = new Date(eventDate)

  const timeDiff = date.getTime() - today.getTime()

  return Math.round(timeDiff / (1000 * 3600 * 24))
}

const getFasterProgressDaysDiff = (values, isMetric) => {
  const { importantEventDate } = values

  const daysTillEventDate = getDaysTillDate(importantEventDate)
  const daysTillGoalDate = getDaysTillGoalDate(values, isMetric)

  // Product requirement:
  // Enable faster progress date only if
  // certain days thresholds presented
  const threshold = daysTillEventDate ? 10 : 35
  const isEnabled = daysTillEventDate
    ? daysTillGoalDate - daysTillEventDate >= threshold
    : daysTillGoalDate >= threshold

  return isEnabled ? 5 : 0
}

const getEventDateWeight = (values, isMetric) => {
  const { importantEventDate, weightKg, weightLbs, goalPaceKg, goalPaceLbs } = values

  // Goal data calculation provided by the product team:
  // eventDateWeight = (weightKg - daysTillEventDate) / goalPaceKg * 7

  let eventDateWeight
  const daysTillEventDate = getDaysTillDate(importantEventDate)

  if (isMetric) {
    eventDateWeight = weightKg - (daysTillEventDate / 7) * goalPaceKg
  } else {
    eventDateWeight = weightLbs - (daysTillEventDate / 7) * goalPaceLbs
  }

  return Math.round(eventDateWeight)
}

const getLocalizedDateLabel = (date, isIncludeYear = true) => {
  const options = { day: "numeric", month: "short" }

  if (isIncludeYear) {
    options.year = "numeric"
  }

  const locale = pipelineConfig.language || "en"
  const localizedDateLabel = date.toLocaleDateString(locale, options)

  return localizedDateLabel
}

const getCompactLocalizedDateLabel = (date) => {
  const options = { month: "short", day: "numeric", year: "numeric" }
  const locale = pipelineConfig.language || "en"
  const compactLocalizedDateLabel = date.toLocaleDateString(locale, options)

  return compactLocalizedDateLabel
}

const getInputDateFormatted = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0") // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

//
// BMI
//
const KGS_IN_LBS = 0.453592
const LBS_IN_KG = 2.20462

const getNormalizedHeight = (values, isMetric) => {
  const { heightCm, heightFt, heightIn } = values

  if (isMetric) {
    return heightCm
  } else {
    return heightFt * 30.48 + heightIn * 2.54
  }
}

const getNormalizedWeight = (values, isMetric) => {
  const { weightKg, weightLbs } = values

  if (isMetric) {
    return weightKg
  } else {
    return weightLbs * KGS_IN_LBS
  }
}

const getBmi = (values, isMetric) => {
  const weight = getNormalizedWeight(values, isMetric)
  const height = getNormalizedHeight(values, isMetric)
  return weight / (height / 100.0) ** 2
}

const getBmiPercents = (bmi) => {
  const percents = ((bmi - 12.01) * 100) / (29.99 - 12.01)

  if (percents > 98) {
    return 98
  } else if (percents < 0) {
    return 0
  }

  return percents.toFixed()
}

const getGoalPaceKgValues = () => [0.5, 1, 2]
const getDefaultGoalPaceKg = () => 1
const getGoalPaceLbsValues = () => [1, 2.5, 4.5]
const getDefaultGoalPaceLbs = () => 2.5

const getOfferId = (offer) => {
  // Offer ID data field depends from BE target system: api-diet or nnp2
  return offer.brandOfferId
    ? offer.brandOfferId
    : offer.priceProductObjectId
}

// Product requirement, quote:
// 5kg or 1/3 of the (current weight - target weight) (whichever is smaller)
const getFirstWeekWeightLoss = ({
  weightKg,
  weightLbs,
  targetWeightKg,
  targetWeightLbs,
  measurementSystem
}) => {
  const isMetric = measurementSystem === "metric"
  const currentWeight = isMetric ? weightKg : weightLbs
  const targetWeight = isMetric ? targetWeightKg : targetWeightLbs

  const targetDiff = currentWeight - targetWeight
  const maxFirstWeekLoss = isMetric ? 5 : 11

  return Math.round(Math.min(maxFirstWeekLoss, targetDiff / 3))
}

// Product requirement, quote:
// Metric: 5kg or (currentWeight - targetWeight)/3 + (3 x maxGoalPace)
// Imperial: 11lbs or (currentWeight - targetWeight)/3 + (3 x maxGoalPace)
// But if the result is grater than difference between currentWeight and targetWeight,
// then that difference should replace the formula in that sentence. 
// So if the difference, for example, is currentWeight - targetWeight = 17 
// but formula sum is 19, then 17 overrides 19.
const getFirstMonthWeightLoss = ({
  weightKg,
  weightLbs,
  targetWeightKg,
  targetWeightLbs,
  measurementSystem
}) => {
  const isMetric = measurementSystem === "metric"
  const currentWeight = isMetric ? weightKg : weightLbs
  const targetWeight = isMetric ? targetWeightKg : targetWeightLbs
  const targetDiff = currentWeight - targetWeight
  const maxGoalPace = isMetric
    ? getGoalPaceKgValues().reverse()[0]
    : getGoalPaceLbsValues().reverse()[0]

  const firstWeekLoss = getFirstWeekWeightLoss({
    weightKg,
    weightLbs,
    targetWeightKg,
    targetWeightLbs,
    measurementSystem
  })

  const result = Math.round(firstWeekLoss + (3 * maxGoalPace))

  return Math.round((result > targetDiff) ? targetDiff : result)
}

export {
  getLocalizedDateLabel,
  getCompactLocalizedDateLabel,
  getDaysTillDate,
  getDaysTillGoalDate,
  getInputDateFormatted,
  getEventDateWeight,
  getNormalizedHeight,
  getNormalizedWeight,
  getBmi,
  getBmiPercents,
  getGoalPaceKgValues,
  getDefaultGoalPaceKg,
  getGoalPaceLbsValues,
  getDefaultGoalPaceLbs,
  getFasterProgressDaysDiff,
  getOfferId,
  getFirstWeekWeightLoss,
  getFirstMonthWeightLoss,
}
